import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Form, Table } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { deleteFix, fetchFixes, fetchSnp500, updateFix } from '../reducers/snp500Slice'

const FIX_COLUMNS = [
  { key: 'original', label: 'Original' },
  { key: 'replacement', label: 'Replacement' },
  { key: 'date', label: 'Date' },
  { key: 'source', label: 'Source' },
  { key: 'status', label: 'Status' },
]

const CONSTITUENT_COLUMNS = [
  { key: 'Security', label: 'Name' },
  { key: 'GICS Sector', label: 'Sector' },
  { key: 'GICS Sub-Industry', label: 'Sub-Industry' },
  { key: 'Headquarters Location', label: 'Headquarters' },
  { key: 'Date added', label: 'Date Added' },
  { key: 'CIK', label: 'CIK' },
  { key: 'Founded', label: 'Founded' },
]

const ALL_SORTABLE = [...FIX_COLUMNS, ...CONSTITUENT_COLUMNS]

const PAGE_SIZE = 25

function Snp500FixesTab() {
  const dispatch = useDispatch()
  const fixes = useSelector((state) => state.snp500.fixes)
  const items = useSelector((state) => state.snp500.items)
  const fixesLoading = useSelector((state) => state.snp500.fixesLoading)
  const fixesError = useSelector((state) => state.snp500.fixesError)

  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')
  const [page, setPage] = useState(1)
  const [editingOriginal, setEditingOriginal] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [editErrors, setEditErrors] = useState({})
  const [deletingOriginal, setDeletingOriginal] = useState(null)
  const [deleteErrors, setDeleteErrors] = useState({})

  useEffect(() => {
    dispatch(fetchFixes())
  }, [dispatch])

  const itemsBySymbol = useMemo(
    () => Object.fromEntries(items.map((i) => [i.Symbol, i])),
    [items]
  )
  const itemSymbols = useMemo(() => new Set(items.map((i) => i.Symbol)), [items])

  const enrichedFixes = useMemo(
    () =>
      fixes.map((fix) => {
        const applied = !itemSymbols.has(fix.original)
        const constituent = applied
          ? itemsBySymbol[fix.replacement] ?? null
          : itemsBySymbol[fix.original] ?? null
        return { ...fix, status: applied ? 'Applied' : 'Pending', constituent }
      }),
    [fixes, itemSymbols, itemsBySymbol]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return enrichedFixes
    return enrichedFixes.filter(
      (f) =>
        f.original.toLowerCase().includes(q) ||
        f.replacement.toLowerCase().includes(q)
    )
  }, [enrichedFixes, search])

  const sorted = useMemo(() => {
    if (!sortKey) return filtered
    const isConstituent = CONSTITUENT_COLUMNS.some((c) => c.key === sortKey)
    return [...filtered].sort((a, b) => {
      const aVal = isConstituent ? (a.constituent?.[sortKey] ?? '') : (a[sortKey] ?? '')
      const bVal = isConstituent ? (b.constituent?.[sortKey] ?? '') : (b[sortKey] ?? '')
      const cmp = String(aVal).localeCompare(String(bVal))
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageItems = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  function startEdit(fix) {
    setEditingOriginal(fix.original)
    setEditValue(fix.replacement)
    setEditErrors((e) => {
      const next = { ...e }
      delete next[fix.original]
      return next
    })
    setDeletingOriginal(null)
  }

  function cancelEdit() {
    setEditingOriginal(null)
    setEditValue('')
  }

  async function saveEdit(original) {
    try {
      await dispatch(updateFix({ original, replacement: editValue })).unwrap()
      setEditingOriginal(null)
      dispatch(fetchSnp500())
      dispatch(fetchFixes())
    } catch (err) {
      setEditErrors((e) => ({ ...e, [original]: err.message || 'Update failed' }))
    }
  }

  function startDelete(original) {
    setDeletingOriginal(original)
    setDeleteErrors((e) => {
      const next = { ...e }
      delete next[original]
      return next
    })
    setEditingOriginal(null)
  }

  function cancelDelete() {
    setDeletingOriginal(null)
  }

  async function confirmDelete(original) {
    try {
      await dispatch(deleteFix(original)).unwrap()
      setDeletingOriginal(null)
    } catch (err) {
      setDeleteErrors((e) => ({ ...e, [original]: err.message || 'Delete failed' }))
    }
  }

  if (fixesLoading) {
    return <div className="text-muted small py-3">Loading fixes…</div>
  }

  if (fixesError) {
    return <Alert variant="danger">{fixesError}</Alert>
  }

  if (fixes.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <p className="mb-0">No fixes on record.</p>
      </div>
    )
  }

  return (
    <>
      <Form.Control
        type="text"
        placeholder="Search by Original or Replacement…"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value)
          setPage(1)
        }}
        className="mb-3"
        aria-label="Search fixes"
      />
      <Table responsive hover size="sm">
        <thead>
          <tr>
            {ALL_SORTABLE.map(({ key, label }) => (
              <th key={key} scope="col">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none text-reset fw-semibold"
                  onClick={() => handleSort(key)}
                >
                  {label}
                  {sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                </button>
              </th>
            ))}
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((fix) => {
            const isEditing = editingOriginal === fix.original
            const isDeleting = deletingOriginal === fix.original
            return (
              <React.Fragment key={fix.original}>
                <tr>
                  <td>{fix.original}</td>
                  <td>
                    {isEditing ? (
                      <Form.Control
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        size="sm"
                        aria-label="Edit replacement"
                      />
                    ) : (
                      fix.replacement
                    )}
                  </td>
                  <td>{fix.date}</td>
                  <td>{fix.source}</td>
                  <td>{fix.status}</td>
                  {CONSTITUENT_COLUMNS.map(({ key }) => (
                    <td key={key}>{fix.constituent?.[key] ?? '—'}</td>
                  ))}
                  <td>
                    {isEditing ? (
                      <>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => saveEdit(fix.original)}
                        >
                          Save
                        </Button>{' '}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : isDeleting ? (
                      <>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => confirmDelete(fix.original)}
                        >
                          Delete
                        </Button>{' '}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={cancelDelete}
                        >
                          Cancel
                        </Button>
                        {deleteErrors[fix.original] && (
                          <Alert variant="danger" className="mb-0 mt-1 py-1 px-2 small">
                            {deleteErrors[fix.original]}
                          </Alert>
                        )}
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => startEdit(fix)}
                        >
                          Edit
                        </Button>{' '}
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => startDelete(fix.original)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
                {isEditing && editErrors[fix.original] && (
                  <tr>
                    <td colSpan={ALL_SORTABLE.length + 1}>
                      <Alert variant="danger" className="mb-0 py-1 px-2 small">
                        {editErrors[fix.original]}
                      </Alert>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <div className="d-flex align-items-center gap-3 mt-2">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={currentPage === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-muted small">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={currentPage === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </>
  )
}

export default Snp500FixesTab
